/**
 * Referral Tracking Utility
 * Handles storing and retrieving referral codes from URL and localStorage
 */

const REFERRAL_STORAGE_KEY = 'fth_referral_code';
const REFERRAL_EXPIRY_KEY = 'fth_referral_expiry';
const REFERRAL_EXPIRY_DAYS = 30;

/**
 * Store referral code from URL in localStorage (30 day expiry)
 */
export const captureReferralFromUrl = () => {
  if (typeof window === 'undefined') return null;
  
  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get('ref');
  
  if (refCode && refCode.length >= 6 && refCode.length <= 12) {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + REFERRAL_EXPIRY_DAYS);
    
    localStorage.setItem(REFERRAL_STORAGE_KEY, refCode.toUpperCase());
    localStorage.setItem(REFERRAL_EXPIRY_KEY, expiry.toISOString());
    
    // Track click
    trackReferralClick(refCode);
    
    return refCode.toUpperCase();
  }
  
  return null;
};

/**
 * Get stored referral code (if not expired and not self-referral)
 * @param {string} currentUserPersonalCode - The logged-in user's own referral code (optional)
 */
export const getStoredReferralCode = (currentUserPersonalCode = null) => {
  if (typeof window === 'undefined') return null;
  
  const code = localStorage.getItem(REFERRAL_STORAGE_KEY);
  const expiry = localStorage.getItem(REFERRAL_EXPIRY_KEY);
  
  if (!code || !expiry) return null;
  
  // Check expiry
  if (new Date() > new Date(expiry)) {
    clearReferralCode();
    return null;
  }
  
  // Self-referral prevention: if the stored code matches the logged-in user's own code, reject it
  if (currentUserPersonalCode && code.toUpperCase() === currentUserPersonalCode.toUpperCase()) {
    clearReferralCode(); // Clear it so it doesn't keep showing up
    return null;
  }
  
  return code;
};

/**
 * Clear stored referral code
 */
export const clearReferralCode = () => {
  if (typeof window === 'undefined') return;
  
  localStorage.removeItem(REFERRAL_STORAGE_KEY);
  localStorage.removeItem(REFERRAL_EXPIRY_KEY);
};

/**
 * Track a referral link click (fire and forget)
 */
export const trackReferralClick = async (code) => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${backendUrl}/api/referrals/track-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ referral_code: code }),
    });
  } catch (err) {
    // Silently fail - don't block user experience
    console.debug('Failed to track referral click:', err);
  }
};

/**
 * Attach referral to a user (after signup)
 */
export const attachReferralToUser = async (token) => {
  const code = getStoredReferralCode();
  if (!code || !token) return false;
  
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const response = await fetch(`${backendUrl}/api/referrals/attach`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ referral_code: code }),
    });
    
    if (response.ok) {
      clearReferralCode(); // Clear after successful attach
      return true;
    }
  } catch (err) {
    console.debug('Failed to attach referral:', err);
  }
  
  return false;
};

/**
 * Build a URL with referral code appended
 */
export const buildReferralUrl = (baseUrl, referralCode) => {
  if (!referralCode) return baseUrl;
  
  const url = new URL(baseUrl, window.location.origin);
  url.searchParams.set('ref', referralCode);
  return url.toString();
};

/**
 * Initialize referral tracking on app load
 */
export const initReferralTracking = () => {
  captureReferralFromUrl();
};

export default {
  captureReferralFromUrl,
  getStoredReferralCode,
  clearReferralCode,
  trackReferralClick,
  attachReferralToUser,
  buildReferralUrl,
  initReferralTracking,
};
